import _get from 'lodash/get'
import * as z from 'zod'

import { HTTP_METHOD } from '@nxt-insurance/api-service'

import { PET_CONSTANTS } from '@flows/configs/petHealth/constants'
import { getBreedWithTranslatableUnknown } from '@flows/configs/petHealth/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import { createPreprocessedEnumBooleanSchema } from '@flows/configs/shared/validators'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_PetHealthV1GetsafeDe } from '@flows/insurance/schemas'

const cross_breed_names = (params: StepConfigParams) =>
  ({
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('first_breed_name')]:
        BaseSchema_PetHealthV1GetsafeDe.shape.first_breed_name.unwrap(),
      [params.helperFunctions.getParamPath('second_breed_name')]:
        BaseSchema_PetHealthV1GetsafeDe.shape.second_breed_name.unwrap(),
      // We need this values to save field disabled after user go step back
      [params.helperFunctions.getTransientPath('unknown_breed_checkbox1')]:
        createPreprocessedEnumBooleanSchema(z.enum(['true', 'false'])).optional(),
      [params.helperFunctions.getTransientPath('unknown_breed_checkbox2')]:
        createPreprocessedEnumBooleanSchema(z.enum(['true', 'false'])).optional(),
    }),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.title',
          values: {
            pet_name: {
              type: 'dynamicValue',
              path: params.helperFunctions.getParamPath('pet_name'),
            },
          },
        },
      },

      {
        type: 'dropDown',
        disabledConditionConfig: {
          condition: {
            valuePath: params.helperFunctions.getTransientPath('unknown_breed_checkbox1'),
            expectedValue: true,
          },
          fallbackValue: PET_CONSTANTS.unknownOption,
        },
        styleType: 'underlined',
        name: params.helperFunctions.getParamPath('first_breed_name'),
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.breed1.label',
        },
        options: params.helperFunctions.getTransientPath('breeds'),
        optionsTransformer: (breeds) =>
          breeds.map((breed) => getBreedWithTranslatableUnknown(String(breed))),
        isOptional: true,
      },
      {
        type: 'dropDown',
        styleType: 'underlined',
        disabledConditionConfig: {
          condition: {
            valuePath: params.helperFunctions.getTransientPath('unknown_breed_checkbox2'),
            expectedValue: true,
          },
          fallbackValue: PET_CONSTANTS.unknownOption,
        },
        name: params.helperFunctions.getParamPath('second_breed_name'),
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.breed2.label',
        },
        options: params.helperFunctions.getTransientPath('breeds'),
        optionsTransformer: (breeds) =>
          breeds.map((breed) => getBreedWithTranslatableUnknown(String(breed))),
        isOptional: true,
      },
      {
        type: 'checkbox',
        blockTitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.unknownBreedSection.title',
        },
        fields: [
          {
            name: params.helperFunctions.getTransientPath('unknown_breed_checkbox1'),
            title: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_petHealth:steps.cross_breed_names.checkboxes.unknownBreed1.title',
            },
          },
          {
            name: params.helperFunctions.getTransientPath('unknown_breed_checkbox2'),
            title: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_petHealth:steps.cross_breed_names.checkboxes.unknownBreed2.title',
            },
          },
        ],
      },

      {
        type: 'infoBox',
        showInfoIcon: true,
        contentConfiguration: [
          {
            type: 'title',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.infoBox.title',
            },
            isSmall: true,
          },
          {
            type: 'text',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.infoBox.text',
            },
          },
        ],
      },
    ],

    onPrepare: {
      type: 'simpleHook',
      callback: async (flowValues: FlowValues) => {
        const response = await params.clientHelpers.apiService.call({
          serviceName: 'insurance',
          method: HTTP_METHOD.GET,
          url: `/api/v2/markets/${params.clientHelpers.getMarket()}/products/${
            params.productKey
          }/configuration_params_options`,
        })

        const petTypeResult = z
          .string()
          .safeParse(_get(flowValues, params.helperFunctions.getParamPath('pet_type')))
        if (petTypeResult.success) {
          const petType = petTypeResult.data
          return {
            formValues: {
              [params.helperFunctions.getTransientPath('breeds')]:
                response.data.first_breed_name[0][petType],
            },
          }
        }
        return {
          formValues: {
            [params.helperFunctions.getTransientPath('breeds')]: undefined,
          },
        }
      },
    },
    subFlow: params.subFlow,
    nextStepConfig: {
      valueFunction: (flowValues) => {
        const breed1 = _get(flowValues, params.helperFunctions.getParamPath('first_breed_name'))
        const breed2 = _get(flowValues, params.helperFunctions.getParamPath('second_breed_name'))
        if (breed1 === PET_CONSTANTS.unknownOption && breed2 === PET_CONSTANTS.unknownOption) {
          return 'shoulder_height'
        }
        return 'pet_age'
      },
      valueToStepName: {
        pet_age: 'pet_age',
        shoulder_height: 'shoulder_height',
      },
    },
  }) as const satisfies AnyStepConfiguration

export default cross_breed_names
